<template>
  <div class="min-h-screen bg-background">
    <Breadcrumb />
    <Navbar />

    <!-- AI专项头部 -->
    <section class="relative pt-20 pb-16 overflow-hidden">
      <div class="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10"></div>
      <div class="container mx-auto px-4 relative z-10">
        <div class="text-center max-w-4xl mx-auto">
          <h1
            class="text-4xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-primary to-primary/60 bg-clip-text text-transparent">
            AI智能解决方案
          </h1>
          <p class="text-xl text-muted-foreground mb-8 leading-relaxed">
            基于深度学习和计算机视觉技术，提供全方位AI智能化解决方案
          </p>
          <div class="flex flex-wrap justify-center gap-4 mb-8">
            <div class="px-4 py-2 bg-primary/10 rounded-full text-primary font-medium">157 TOPS算力</div>
            <div class="px-4 py-2 bg-primary/10 rounded-full text-primary font-medium">NVIDIA Jetson Orin NX</div>
            <div class="px-4 py-2 bg-primary/10 rounded-full text-primary font-medium">边缘AI计算</div>
            <div class="px-4 py-2 bg-primary/10 rounded-full text-primary font-medium">128GB NVMe SSD</div>
          </div>
        </div>
      </div>
    </section>

    <!-- 核心产品：视频算法盒子 -->
    <section class="py-16 bg-muted/30">
      <div class="container mx-auto px-4">
        <div class="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
          <!-- 左侧文本 -->
          <div class="flex-1 space-y-6">
            <h1
              class="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              AIBOX-OrinNX 边缘计算模组
            </h1>
            <div class="space-y-4">
              <p class="text-xl text-muted-foreground">
                搭载NVIDIA Jetson Orin NX边缘计算模组，配置八核ARM CPU和32个Tensor core的1024核NVIDIA Ampere架构GPU，提供高达157
                TOPS的AI算力，支持多个并发AI应用程序管道
              </p>
              <ul class="space-y-2">
                <li class="flex items-center gap-2 text-muted-foreground">
                  <div class="w-2 h-2 rounded-full bg-primary"></div>
                  <span>智能识别化工区域危险品泄露、违规操作、安全隐患等</span>
                </li>
                <li class="flex items-center gap-2 text-muted-foreground">
                  <div class="w-2 h-2 rounded-full bg-primary"></div>
                  <span>精准识别人员位置、行为模式、违规操作</span>
                </li>
                <li class="flex items-center gap-2 text-muted-foreground">
                  <div class="w-2 h-2 rounded-full bg-primary"></div>
                  <span>根据具体应用场景，定制开发专用AI识别模型</span>
                </li>
                <li class="flex items-center gap-2 text-muted-foreground">
                  <div class="w-2 h-2 rounded-full bg-primary"></div>
                  <span>完整的AI软件堆栈和生态系统支持</span>
                </li>
              </ul>
              <div class="flex gap-4 pt-4">
                <Button size="lg"
                  class="bg-primary hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl">
                  免费咨询方案
                </Button>
                <Button size="lg" variant="outline" class="hover:bg-primary/10 transition-all duration-300">
                  查看产品演示
                </Button>
              </div>
            </div>
          </div>

          <!-- 右侧图片 -->
          <div class="flex-1 relative">
            <div class="absolute inset-0 bg-gradient-to-r from-primary/10 to-primary/5 rounded-3xl blur-3xl"></div>
            <img src="@/assets/ai/c3f4d182aac06e017f70892085d39356.png" alt="AIBOX-OrinNX 高性能边缘计算模组"
              class="relative z-10 w-full h-auto rounded-xl shadow-2xl transition-transform duration-500 hover:scale-105" />
          </div>
        </div>
      </div>
    </section>

    <!-- AI软件堆栈 -->
    <section class="py-16">
      <div class="container mx-auto px-4">
        <h2
          class="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
          AI软件堆栈与生态系统
        </h2>

        <div class="bg-muted/50 rounded-xl p-8 mb-8">
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div
              class="flex items-center gap-2 p-3 bg-background rounded-lg border border-border hover:shadow-md transition-all duration-300">
              <div class="w-3 h-3 bg-green-500 rounded-full"></div>
              <span class="font-medium">NVIDIA CUDA</span>
            </div>
            <div
              class="flex items-center gap-2 p-3 bg-background rounded-lg border border-border hover:shadow-md transition-all duration-300">
              <div class="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span class="font-medium">TensorRT</span>
            </div>
            <div
              class="flex items-center gap-2 p-3 bg-background rounded-lg border border-border hover:shadow-md transition-all duration-300">
              <div class="w-3 h-3 bg-purple-500 rounded-full"></div>
              <span class="font-medium">CuDNN</span>
            </div>
            <div
              class="flex items-center gap-2 p-3 bg-background rounded-lg border border-border hover:shadow-md transition-all duration-300">
              <div class="w-3 h-3 bg-orange-500 rounded-full"></div>
              <span class="font-medium">JetPack SDK</span>
            </div>
            <div
              class="flex items-center gap-2 p-3 bg-background rounded-lg border border-border hover:shadow-md transition-all duration-300">
              <div class="w-3 h-3 bg-red-500 rounded-full"></div>
              <span class="font-medium">Isaac ROS</span>
            </div>
            <div
              class="flex items-center gap-2 p-3 bg-background rounded-lg border border-border hover:shadow-md transition-all duration-300">
              <div class="w-3 h-3 bg-teal-500 rounded-full"></div>
              <span class="font-medium">Metropolis</span>
            </div>
          </div>
        </div>

        <p class="text-center text-muted-foreground max-w-3xl mx-auto">
          借助全面的AI软件堆栈和生态系统，在边缘生成式AI以及NVIDIA Metropolis和Isaac平台的支持下，使边缘AI和机器人开发实现大众化。无需依赖昂贵的内部AI资源即可将先进的技术无缝集成到产品中。
        </p>
      </div>
    </section>

    <!-- AI产品矩阵 -->
    <section class="py-16 bg-muted/30">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2
            class="text-3xl lg:text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            AI产品矩阵
          </h2>
          <p class="text-xl text-muted-foreground max-w-3xl mx-auto">
            覆盖工业4.0全场景的AI智能解决方案，从视觉检测到智能分析，助力企业数字化转型
          </p>
        </div>

        <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <!-- 产品视检 -->
          <Card class="group hover:shadow-lg transition-all duration-300 h-full">
            <div class="relative h-32 bg-muted/50 flex items-center justify-center">
              <div class="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                <div class="text-2xl text-primary">🔍</div>
              </div>
            </div>
            <CardHeader class="pb-3">
              <CardTitle class="text-center text-lg">产品质量视检</CardTitle>
            </CardHeader>
            <CardContent>
              <p class="text-muted-foreground text-center text-sm leading-relaxed mb-3">基于机器视觉的产品质量检测，自动识别缺陷、尺寸测量、外观检验
              </p>
              <div class="flex flex-wrap gap-1 justify-center">
                <Badge variant="secondary" class="text-xs">缺陷检测</Badge>
                <Badge variant="secondary" class="text-xs">尺寸测量</Badge>
              </div>
            </CardContent>
          </Card>

          <!-- AI大模型训练 -->
          <Card class="group hover:shadow-lg transition-all duration-300 h-full">
            <div class="relative h-32 bg-muted/50 flex items-center justify-center">
              <div class="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                <div class="text-2xl text-primary">🧠</div>
              </div>
            </div>
            <CardHeader class="pb-3">
              <CardTitle class="text-center text-lg">AI大模型训练</CardTitle>
            </CardHeader>
            <CardContent>
              <p class="text-muted-foreground text-center text-sm leading-relaxed mb-3">专业AI模型训练服务，深度学习算法优化，提供定制化模型解决方案
              </p>
              <div class="flex flex-wrap gap-1 justify-center">
                <Badge variant="secondary" class="text-xs">深度学习</Badge>
                <Badge variant="secondary" class="text-xs">模型优化</Badge>
              </div>
            </CardContent>
          </Card>

          <!-- AI物品分类 -->
          <Card class="group hover:shadow-lg transition-all duration-300 h-full">
            <div class="relative h-32 bg-muted/50 flex items-center justify-center">
              <div class="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                <div class="text-2xl text-primary">📦</div>
              </div>
            </div>
            <CardHeader class="pb-3">
              <CardTitle class="text-center text-lg">AI智能分拣</CardTitle>
            </CardHeader>
            <CardContent>
              <p class="text-muted-foreground text-center text-sm leading-relaxed mb-3">智能物品识别与分类，支持多类别物体检测，自动化分拣和管理</p>
              <div class="flex flex-wrap gap-1 justify-center">
                <Badge variant="secondary" class="text-xs">物体识别</Badge>
                <Badge variant="secondary" class="text-xs">自动分拣</Badge>
              </div>
            </CardContent>
          </Card>

          <!-- 人流量车流量检测 -->
          <Card class="group hover:shadow-lg transition-all duration-300 h-full">
            <div class="relative h-32 bg-muted/50 flex items-center justify-center">
              <div class="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                <div class="text-2xl text-primary">🚗</div>
              </div>
            </div>
            <CardHeader class="pb-3">
              <CardTitle class="text-center text-lg">智能流量统计</CardTitle>
            </CardHeader>
            <CardContent>
              <p class="text-muted-foreground text-center text-sm leading-relaxed mb-3">实时人流量、车流量统计分析，支持热力图分析和趋势预测</p>
              <div class="flex flex-wrap gap-1 justify-center">
                <Badge variant="secondary" class="text-xs">流量统计</Badge>
                <Badge variant="secondary" class="text-xs">趋势分析</Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        <!-- 应用场景统计 -->
        <div class="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div class="text-center">
            <div class="text-3xl font-bold text-primary mb-2">500+</div>
            <div class="text-muted-foreground">成功案例</div>
          </div>
          <div class="text-center">
            <div class="text-3xl font-bold text-primary mb-2">99.9%</div>
            <div class="text-muted-foreground">识别精度</div>
          </div>
          <div class="text-center">
            <div class="text-3xl font-bold text-primary mb-2">24/7</div>
            <div class="text-muted-foreground">全天候运行</div>
          </div>
          <div class="text-center">
            <div class="text-3xl font-bold text-primary mb-2">50+</div>
            <div class="text-muted-foreground">行业应用</div>
          </div>
        </div>
      </div>
    </section>

    <!-- 技术优势 -->
    <section class="py-16 bg-muted/30">
      <div class="container mx-auto px-4">
        <h2 class="text-3xl font-bold text-center mb-12">技术优势</h2>

        <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div
            class="text-center space-y-4 p-6 bg-background rounded-xl border border-border hover:shadow-lg transition-all duration-300">
            <div
              class="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
              <div class="text-2xl text-white">⚡</div>
            </div>
            <h3 class="text-xl font-semibold">超强算力</h3>
            <p class="text-muted-foreground text-sm">157 TOPS AI算力，支持复杂模型实时推理，处理多路高清视频流</p>
          </div>

          <div
            class="text-center space-y-4 p-6 bg-background rounded-xl border border-border hover:shadow-lg transition-all duration-300">
            <div
              class="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
              <div class="text-2xl text-white">🏭</div>
            </div>
            <h3 class="text-xl font-semibold">工业级稳定</h3>
            <p class="text-muted-foreground text-sm">专业工业设计，7×24小时稳定运行，适应恶劣环境条件</p>
          </div>

          <div
            class="text-center space-y-4 p-6 bg-background rounded-xl border border-border hover:shadow-lg transition-all duration-300">
            <div
              class="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
              <div class="text-2xl text-white">🚀</div>
            </div>
            <h3 class="text-xl font-semibold">快速部署</h3>
            <p class="text-muted-foreground text-sm">完整的AI软件堆栈，无需复杂配置，即插即用快速部署</p>
          </div>

          <div
            class="text-center space-y-4 p-6 bg-background rounded-xl border border-border hover:shadow-lg transition-all duration-300">
            <div
              class="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
              <div class="text-2xl text-white">🔧</div>
            </div>
            <h3 class="text-xl font-semibold">灵活定制</h3>
            <p class="text-muted-foreground text-sm">支持模型定制开发，快速适配各种应用场景和业务需求</p>
          </div>
        </div>

        <!-- 性能对比 -->
        <div class="mt-16">
          <h3 class="text-2xl font-bold text-center mb-8">性能对比</h3>
          <div class="bg-background rounded-xl border border-border p-8">
            <div class="overflow-x-auto">
              <table class="w-full text-left">
                <thead>
                  <tr class="border-b border-border">
                    <th class="pb-4 text-foreground font-semibold">规格</th>
                    <th class="pb-4 text-center text-primary font-semibold">AIBOX-OrinNX</th>
                    <th class="pb-4 text-center text-muted-foreground">传统方案</th>
                  </tr>
                </thead>
                <tbody class="text-sm">
                  <tr class="border-b border-border/50">
                    <td class="py-3 text-muted-foreground">AI算力</td>
                    <td class="py-3 text-center font-bold text-primary">157 TOPS</td>
                    <td class="py-3 text-center text-muted-foreground">20-50 TOPS</td>
                  </tr>
                  <tr class="border-b border-border/50">
                    <td class="py-3 text-muted-foreground">功耗</td>
                    <td class="py-3 text-center font-medium text-green-600">25W</td>
                    <td class="py-3 text-center text-muted-foreground">100W+</td>
                  </tr>
                  <tr class="border-b border-border/50">
                    <td class="py-3 text-muted-foreground">视频处理</td>
                    <td class="py-3 text-center font-medium text-blue-600">8路 4K</td>
                    <td class="py-3 text-center text-muted-foreground">2-4路 1080P</td>
                  </tr>
                  <tr>
                    <td class="py-3 text-muted-foreground">部署难度</td>
                    <td class="py-3 text-center font-medium text-primary">即插即用</td>
                    <td class="py-3 text-center text-muted-foreground">复杂配置</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 应用案例 -->
    <section class="py-16">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2
            class="text-3xl lg:text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            成功应用案例
          </h2>
          <p class="text-xl text-muted-foreground max-w-3xl mx-auto">
            服务500+企业客户，覆盖50+行业领域，助力企业实现智能化转型升级
          </p>
        </div>

        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <!-- 化工厂案例 -->
          <Card class="overflow-hidden hover:shadow-lg transition-all duration-300">
            <div class="h-32 bg-muted/50 flex items-center justify-center">
              <div class="text-center">
                <div class="text-4xl mb-2">🏭</div>
                <div class="text-lg font-semibold">化工厂安全监控</div>
              </div>
            </div>
            <CardContent class="p-6">
              <div class="flex items-center gap-2 mb-3">
                <h3 class="text-lg font-semibold">某大型化工企业</h3>
                <Badge variant="outline" class="text-xs">重点项目</Badge>
              </div>
              <p class="text-muted-foreground mb-4 leading-relaxed">部署AI视频算法盒子进行全天候安全监控，实现危险品识别、人员行为分析、安全事故预警。</p>
              <div class="space-y-2 mb-4">
                <div class="flex justify-between text-sm">
                  <span class="text-muted-foreground">事故率降低</span>
                  <span class="font-semibold text-primary">95%</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-muted-foreground">响应时间</span>
                  <span class="font-semibold text-primary">&lt;30秒</span>
                </div>
              </div>
              <div class="flex gap-2 flex-wrap">
                <Badge variant="secondary">事故预警</Badge>
                <Badge variant="secondary">行为识别</Badge>
              </div>
            </CardContent>
          </Card>

          <!-- 智慧园区案例 -->
          <Card class="overflow-hidden hover:shadow-lg transition-all duration-300">
            <div class="h-32 bg-muted/50 flex items-center justify-center">
              <div class="text-center">
                <div class="text-4xl mb-2">🏢</div>
                <div class="text-lg font-semibold">智慧园区管理</div>
              </div>
            </div>
            <CardContent class="p-6">
              <div class="flex items-center gap-2 mb-3">
                <h3 class="text-lg font-semibold">科技产业园区</h3>
                <Badge variant="outline" class="text-xs">标杆案例</Badge>
              </div>
              <p class="text-muted-foreground mb-4 leading-relaxed">集成人流量检测、车流量分析、访客管理等功能，提升园区智能化管理水平。</p>
              <div class="space-y-2 mb-4">
                <div class="flex justify-between text-sm">
                  <span class="text-muted-foreground">管理效率提升</span>
                  <span class="font-semibold text-primary">80%</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-muted-foreground">运营成本降低</span>
                  <span class="font-semibold text-primary">60%</span>
                </div>
              </div>
              <div class="flex gap-2 flex-wrap">
                <Badge variant="secondary">流量统计</Badge>
                <Badge variant="secondary">智能分析</Badge>
              </div>
            </CardContent>
          </Card>

          <!-- 制造业案例 -->
          <Card class="overflow-hidden hover:shadow-lg transition-all duration-300">
            <div class="h-32 bg-muted/50 flex items-center justify-center">
              <div class="text-center">
                <div class="text-4xl mb-2">⚙️</div>
                <div class="text-lg font-semibold">智能制造检测</div>
              </div>
            </div>
            <CardContent class="p-6">
              <div class="flex items-center gap-2 mb-3">
                <h3 class="text-lg font-semibold">精密制造企业</h3>
                <Badge variant="outline" class="text-xs">质量标杆</Badge>
              </div>
              <p class="text-muted-foreground mb-4 leading-relaxed">部署AI视觉检测系统，实现产品质量自动检测，大幅提升检测精度和效率。</p>
              <div class="space-y-2 mb-4">
                <div class="flex justify-between text-sm">
                  <span class="text-muted-foreground">检测精度</span>
                  <span class="font-semibold text-primary">99.9%</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-muted-foreground">检测速度</span>
                  <span class="font-semibold text-primary">10倍提升</span>
                </div>
              </div>
              <div class="flex gap-2 flex-wrap">
                <Badge variant="secondary">质量检测</Badge>
                <Badge variant="secondary">自动化</Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        <!-- 客户评价 -->
        <div class="mt-16 bg-muted/30 rounded-xl p-8">
          <h3 class="text-2xl font-bold text-center mb-8">客户评价</h3>
          <div class="grid md:grid-cols-3 gap-6">
            <div class="text-center p-6 bg-background rounded-lg">
              <p class="text-muted-foreground mb-4">"AI算法盒子的部署让我们的安全管理水平得到了质的提升，事故率显著降低。"</p>
              <div class="font-semibold">— 化工企业安全总监</div>
            </div>
            <div class="text-center p-6 bg-background rounded-lg">
              <p class="text-muted-foreground mb-4">"系统稳定性非常好，7×24小时运行无故障，技术团队响应也很及时。"</p>
              <div class="font-semibold">— 园区运营经理</div>
            </div>
            <div class="text-center p-6 bg-background rounded-lg">
              <p class="text-muted-foreground mb-4">"检测精度和速度都超出了我们的预期，投资回报率很高。"</p>
              <div class="font-semibold">— 制造企业质量经理</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 联系我们 -->
    <section class="py-16 bg-muted/30">
      <div class="container mx-auto px-4 text-center">
        <h2 class="text-3xl font-bold mb-6">开启AI智能化之旅</h2>
        <p class="text-muted-foreground mb-8 max-w-2xl mx-auto">
          无论您需要定制化AI模型开发，还是现成的智能解决方案，我们都能为您提供专业的服务
        </p>
        <div class="flex gap-4 justify-center">
          <Button size="lg" class="bg-primary hover:bg-primary/90">
            免费咨询方案
          </Button>
          <Button size="lg" variant="outline">
            查看产品演示
          </Button>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import Navbar from '@/components/Navbar.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import Footer from '@/components/Footer.vue'
</script>

<style scoped>
/* 添加一些自定义动画效果 */
@keyframes float {

  0%,
  100% {
    transform: translateY(0px);
  }

  50% {
    transform: translateY(-10px);
  }
}

.float-animation {
  animation: float 3s ease-in-out infinite;
}
</style>
